import apiClient from "../lib/api/client";

export type PipelineCommand = string;

export interface SimulationConfig {
  id: number;
  name: string;
  is_current: boolean;
  parameters: any[];
  created_by: string;
  base_date_start: string;
  base_date_end: string;
  forecast_date_start: string;
  forecast_date_end: string;
  pipeline_config: null | PipelineCommand[] | Record<string, unknown>;
  last_pipeline_execution_id: number | null;
}

export interface AvailableStep {
  order: number;
  command: PipelineCommand;
  name: string;
  description: string;
  enabled: boolean;
  options: any[];
  is_parallel?: boolean;
  estimated_duration?: string;
}

export interface AvailableStepsResponse {
  steps: AvailableStep[];
}

export interface SimulationResultItem {
  id: number;
  book_id: number;
  book_title: string;
  book_isbn: string;
  sk_code: number;
  product_quantity: number;
  main_product_quantity: number;
  secondary_product_quantity: number;
}

export interface SimulationResultCategory {
  category_id: number;
  category_code: string;
  items: SimulationResultItem[];
}

export interface SimulationResultMonth {
  month: number;
  categories: SimulationResultCategory[];
}

export interface SimulationResultYear {
  year: number;
  months: SimulationResultMonth[];
}

export interface SimulationResult {
  schema_version: number;
  simulation_id: number;
  years: SimulationResultYear[];
}

export interface SimulationsListResponseNormalized {
  message: string;
  data: SimulationConfig[];
}

export interface CreateSimulationBody {
  name: string;
  parameters: any[];
  base_date_start: string;
  base_date_end: string;
  forecast_date_start: string;
  forecast_date_end: string;
  pipeline_config: PipelineCommand[];
}

export interface UpdateSimulationBody extends Partial<CreateSimulationBody> {}

function normalizeListResponse(raw: unknown): SimulationsListResponseNormalized {
  if (!raw || typeof raw !== "object") {
    return { message: "", data: [] };
  }

  const r = raw as Record<string, any>;
  const message = typeof r.message === "string" ? r.message : "";

  // Envelope possível: { message, data: [ ... ] } ou { message, data: { data: [ ... ] } }
  const directList = r.data;
  const list = Array.isArray(directList)
    ? directList
    : Array.isArray(directList?.data)
      ? directList.data
      : [];

  return {
    message,
    data: list as SimulationConfig[],
  };
}

export const simulationsService = {
  async getList(): Promise<SimulationsListResponseNormalized> {
    const response = await apiClient.get<unknown>("/simulations");
    return normalizeListResponse(response);
  },

  async getById(id: number): Promise<SimulationConfig> {
    const response = await apiClient.get<unknown>(`/simulations/${id}`);
    // Expected: { message, data: { ...config } }
    if (response && typeof response === "object") {
      const r = response as Record<string, any>;
      return (r.data ?? r) as SimulationConfig;
    }
    return response as SimulationConfig;
  },

  async create(body: CreateSimulationBody): Promise<SimulationConfig> {
    const response = await apiClient.post<unknown>("/simulations", body);
    const r = response as Record<string, any>;
    return (r.data ?? response) as SimulationConfig;
  },

  async update(id: number, body: UpdateSimulationBody): Promise<SimulationConfig> {
    const response = await apiClient.put<unknown>(`/simulations/${id}`, body);
    const r = response as Record<string, any>;
    return (r.data ?? response) as SimulationConfig;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/simulations/${id}`);
  },

  async setCurrent(id: number): Promise<SimulationConfig> {
    const response = await apiClient.patch<unknown>(`/simulations/${id}/set-current`);
    const r = response as Record<string, any>;
    return (r.data ?? response) as SimulationConfig;
  },

  async getAvailablePipelineSteps(): Promise<AvailableStepsResponse> {
    const response = await apiClient.get<AvailableStepsResponse>("/pipeline/available-steps");
    return response;
  },

  async getResult(id: number): Promise<SimulationResult> {
    const response = await apiClient.get<unknown>(`/simulations/${id}/result`);
    const r = response as Record<string, any>;
    const payload = r.data ?? r;
    return {
      schema_version: Number(payload.schema_version ?? 1),
      simulation_id: Number(payload.simulation_id ?? id),
      years: Array.isArray(payload.years) ? payload.years : [],
    } as SimulationResult;
  },
};

export default simulationsService;

